import React, { useState, useEffect } from "react";
import {
  useGetAssignmentByClusterQuery,
  useGetCampusQuery,
  useGetClustersByCampusQuery,
  useGetStaffByEmailIdQuery,
  useGetStudentsResultsQuery,
  useGetAllCoursesByClusterQuery,
  useGetAllAssignmentQuestionQuery,
} from "../../../../graphql/generated.tsx";
import { Table, Button } from "flowbite-react";
import ReactSelect from "react-select";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../Layout/index.jsx";
import * as XLSX from "xlsx";
import "jspdf-autotable";
import { MdOutlinePersonOff } from "react-icons/md";
import { FaDownload } from "react-icons/fa6";
import { MdTableChart } from "react-icons/md";
import { IoHelpCircleOutline } from "react-icons/io5";
import { LuMousePointerClick } from "react-icons/lu";
import LightSpinner from "../../../../components/LightSpinner";
import ReusableTour from "../../../../components/Tour/index.jsx";
import Pagination from "../../../../components/Pagination/index.jsx";
import ModalUI from "../../../Assignments/Components/ModalUI.jsx";

const staffFilterSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 6,
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
    boxShadow: state.isFocused ? "0 0 0 1px #9ca3af" : "none",
    "&:hover": {
      borderColor: "#9ca3af",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 8px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    paddingRight: 2,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 6,
  }),
  singleValue: (base) => ({
    ...base,
    color: "#111827",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#d1d5db"
      : state.isFocused
      ? "#f3f4f6"
      : "#ffffff",
    color: "#111827",
    ":active": {
      backgroundColor: "#d1d5db",
    },
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 30,
  }),
};

const AllStudentsInCampus = () => {
  const navigate = useNavigate();
  const email = JSON.parse(localStorage.getItem("user"))?.email;
  const helperMessage = "Assignment and course progress of each student in the selected batch.";
  const [shouldFetch, setShouldFetch] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(true);
  const [typedHelperText, setTypedHelperText] = useState("");
  const [shouldStartTyping, setShouldStartTyping] = useState(false);

  const { data: staffData, loading: staffLoading } = useGetStaffByEmailIdQuery({
    variables: { email },
  });

  const { data: campusDetails, loading: campusLoading } = useGetCampusQuery({
    variables: { id: staffData?.getStaffByEmailId?.campusId },
    skip: staffLoading,
  });

  const campus = campusDetails?.campus;

  const [page, setPage] = useState(1);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");

  const limit = 30;

  const { data: clusters, loading: clusterLoading } =
    useGetClustersByCampusQuery({
      variables: { campusName: campus?.name, limit: 200 },
      skip: campusLoading || !shouldFetch,
    });

  const availableClusters = staffData?.getStaffByEmailId?.isAdminStaff
    ? clusters?.getClustersByCampus
    : clusters?.getClustersByCampus.filter((cluster) =>
        staffData?.getStaffByEmailId?.clusterIds?.includes(cluster.id)
      );

  const sortedClusters = [...availableClusters || []].sort((a, b) => a.name.localeCompare(b.name));
  const clusterOptions = sortedClusters.map((cluster) => ({ value: cluster.id, label: cluster.name }));
  const dateFilterOptions = [
    { value: "", label: "Till Date" },
    { value: "15", label: "Last 15 Days" },
    { value: "30", label: "Last 30 Days" },
    { value: "90", label: "Last 3 Months" },
    { value: "custom", label: "Custom Range" },
  ];

  const [selectedClusterId, setSelectedClusterId] = useState(
    sortedClusters?.[0]?.id
  );

  useEffect(() => {
    if (!selectedClusterId && sortedClusters?.length) {
      setSelectedClusterId(sortedClusters[0].id);
    }
  }, [selectedClusterId, sortedClusters]);

  const { loading, error, data, refetch } = useGetStudentsResultsQuery({
    variables: {
      campusId: campus?.id,
      page,
      limit,
      clusterid: selectedClusterId || sortedClusters?.[0]?.id,
    },
    skip: clusterLoading || !shouldFetch,
  });

  const { data: assignmentData, loading: assignmentLoading } =
    useGetAssignmentByClusterQuery({
      variables: {
        campusName: campus?.name,
        clusterId: selectedClusterId || availableClusters?.[0]?.id,
      },
      skip: loading || !shouldFetch,
    });

  const { data: courseData, loading: courseLoading } =
    useGetAllCoursesByClusterQuery({
      variables: {
        campusName: campus?.name,
        clusterId: selectedClusterId || availableClusters?.[0]?.id,
      },
      skip: assignmentLoading || !shouldFetch,
    });

  useEffect(() => {
    if (!shouldFetch) return;
    if (
      loading ||
      assignmentLoading ||
      courseLoading ||
      clusterLoading ||
      staffLoading ||
      campusLoading
    )
      return;

    setShouldStartTyping(true);
  }, [
    shouldFetch,
    loading,
    assignmentLoading,
    courseLoading,
    clusterLoading,
    staffLoading,
    campusLoading,
  ]);

  useEffect(() => {
    if (!shouldStartTyping) return;

    setTypedHelperText("");
    let currentIndex = 0;

    const typingTimer = setInterval(() => {
      currentIndex += 1;
      setTypedHelperText(helperMessage.slice(0, currentIndex));

      if (currentIndex >= helperMessage.length) {
        clearInterval(typingTimer);
      }
    }, 40);

    return () => clearInterval(typingTimer);
  }, [shouldStartTyping, helperMessage]);

  // Filter assignments by date
  const getFilteredAssignments = () => {
    const assignments = assignmentData?.getAssignmentByCluster || [];
    
    if (!dateFilter) return assignments;

    const now = new Date();
    let startDate;

    switch (dateFilter) {
      case "15":
        startDate = new Date();
        startDate.setDate(now.getDate() - 15);
        return assignments.filter((assignment) => {
          const dueDate = new Date(assignment.due_date);
          return dueDate >= startDate && dueDate <= now;
        });

      case "30":
        startDate = new Date();
        startDate.setDate(now.getDate() - 30);
        return assignments.filter((assignment) => {
          const dueDate = new Date(assignment.due_date);
          return dueDate >= startDate && dueDate <= now;
        });

      case "90":
        startDate = new Date();
        startDate.setMonth(now.getMonth() - 3);
        return assignments.filter((assignment) => {
          const dueDate = new Date(assignment.due_date);
          return dueDate >= startDate && dueDate <= now;
        });

      case "custom":
        if (customFromDate && customToDate) {
          const from = new Date(customFromDate);
          const to = new Date(customToDate);
          return assignments.filter((assignment) => {
            const dueDate = new Date(assignment.due_date);
            return dueDate >= from && dueDate <= to;
          });
        }
        return assignments;

      default:
        return assignments;
    }
  };

  const filteredAssignments = getFilteredAssignments();

  // State to store assignment overall marks
  const [assignmentOverallMarks, setAssignmentOverallMarks] = useState({});
  const [questionsLoading, setQuestionsLoading] = useState(false);

  // Fetch questions for all assignments and calculate overall marks
  useEffect(() => {
    const fetchAssignmentQuestions = async () => {
      if (!assignmentData?.getAssignmentByCluster || assignmentLoading) return;

      
      setQuestionsLoading(true);
      const marksMap = {};
      
      try {
        // Import the GraphQL client if needed
        const apolloModule = await import('../../../../utils/apolloClient.js');
        const client = apolloModule.client || apolloModule.default;
        const { GetAllAssignmentQuestionDocument } = await import('../../../../graphql/generated.tsx');
        

        
        for (const assignment of assignmentData.getAssignmentByCluster) {
          try {

            const result = await client.query({
              query: GetAllAssignmentQuestionDocument,
              variables: { assignmentId: assignment.id, limit: 1000 },
            });
            
            
            
            if (result.data?.getAllAssignmentQuestion) {
              const questions = result.data.getAllAssignmentQuestion;
              const overallMarks = questions.reduce((total, question) => {
                const firstTestCaseWeightage = question.test_cases?.[0]?.weightage || 0;
                return total + (firstTestCaseWeightage * 25);
              }, 0);
              marksMap[assignment.id] = overallMarks;
             
            } else {
              marksMap[assignment.id] = 0;
            }
          } catch (error) {
            console.error(`Error fetching questions for assignment ${assignment.id}:`, error);
            marksMap[assignment.id] = 0;
          }
        }
        
        
        setAssignmentOverallMarks(marksMap);
        setQuestionsLoading(false);
      } catch (importError) {
        console.error('Error importing modules:', importError);
        setQuestionsLoading(false);
      }
    };

    fetchAssignmentQuestions();
  }, [assignmentData, assignmentLoading]);

  const handleClusterChange = (value) => {
    setSelectedClusterId(value);
    setPage(1);
    refetch({
      campusId: campus?.id,
      page: 1,
      limit,
      clusterid: value,
    });
  };

  const totalPages = Math.max(
    1,
    Math.ceil((data?.getStudentsResults?.total || 0) / limit)
  );



  const fetchAllStudentsData = async (clusterId) => {
    // Use the same fallback logic as the table query
    const effectiveClusterId =
      clusterId || selectedClusterId || sortedClusters?.[0]?.id;

    if (!effectiveClusterId) {
      console.error("No valid cluster id found for fetching students");
      throw new Error("No valid cluster selected");
    }

    let allStudents = [];
    let currentPage = 1;
    // Use 60 for Excel export to avoid backend errors with larger limits
    const batchSize = 60;
    let hasMore = true;

    while (hasMore) {
      try {
        const { data: pageData } = await refetch({
          campusId: campus?.id,
          clusterid: effectiveClusterId,
          page: currentPage,
          limit: batchSize,
        });

        const students = pageData?.getStudentsResults?.students || [];
        const total = pageData?.getStudentsResults?.total || 0;

        if (students.length > 0) {
          allStudents.push(...students);
        }

        if (allStudents.length >= total || students.length < batchSize) {
          hasMore = false;
        } else {
          currentPage++;
        }
      } catch (error) {
        console.error("An error occurred during batched fetching:", error);
        // Stop fetching further pages but keep already-fetched students
        hasMore = false;
      }
    }
    return allStudents;
  };

  const exportToExcel = async () => {
    setLoadingExcel(true);

    try {
      const allStudentData = await fetchAllStudentsData(selectedClusterId);

      if (!allStudentData.length) {
        throw new Error("No student data could be fetched for export");
      }

      const formatHeaderToTwoLines = (text = "") => {
        const clean = String(text).trim();
        if (clean.length <= 24) return clean;
        const midpoint = Math.floor(clean.length / 2);
        const leftSpace = clean.lastIndexOf(" ", midpoint);
        const rightSpace = clean.indexOf(" ", midpoint);
        const splitIndex =
          leftSpace > 10
            ? leftSpace
            : rightSpace > 0
              ? rightSpace
              : 24;
        return `${clean.slice(0, splitIndex)}\n${clean.slice(splitIndex + 1)}`;
      };

      const assignmentHeaders =
        filteredAssignments?.map((assignment) => {
          const overallMark = assignmentOverallMarks[assignment.id];
          const namePart = formatHeaderToTwoLines(assignment?.name || "");
          const overallPart = overallMark ? `\n(Overall: ${overallMark})` : "";
          return `${namePart}${overallPart}`;
        }) || [];

      const courseHeaders =
        courseData?.getAllCoursesByCluster?.flatMap((course) => [
          `${course?.name} percentage`,
          `${course?.name} overall`,
        ]) || [];

      const headers = [
        "Student Name",
        "Roll Number",
        ...assignmentHeaders,
        ...courseHeaders,
      ];

      const studentData = allStudentData.map((student) => {
        const assignmentScores = filteredAssignments?.map(
          (assignment) => {
            const studentAssignment = student?.assignmentDetails?.find(
              (assign) => assign.assignment_id === assignment?.id
            );
            return studentAssignment?.totalScore || "-";
          }
        );

        const courseProgress = courseData?.getAllCoursesByCluster?.flatMap(
          (course) => {
            const studentCourse = student?.courseDetails?.courseDetails.find(
              (courseDetail) => courseDetail.courseId === course?.id
            );
            return [
              ` ${Math.floor(studentCourse?.percentageCompleted || 0)}%`,
              `${studentCourse?.completedQuestion || 0}/${
                studentCourse?.totalQuestion || 0
              }`,
            ];
          }
        );

        return [
          student?.name || "-",
          student?.rollNo || "-",
          ...assignmentScores,
          ...courseProgress,
        ];
      });

      const excelData = [headers, ...studentData];
      const worksheet = XLSX.utils.aoa_to_sheet(excelData);

      // Make header row taller and set readable column widths so multiline headers are visible.
      worksheet["!rows"] = [{ hpx: 48 }];
      const assignmentColCount = assignmentHeaders.length;
      const courseColCount = courseHeaders.length;
      worksheet["!cols"] = [
        { wch: 18 },
        { wch: 14 },
        ...Array.from({ length: assignmentColCount }, () => ({ wch: 26 })),
        ...Array.from({ length: courseColCount }, () => ({ wch: 20 })),
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Students");

      XLSX.writeFile(workbook, `${campus?.name}_students_data.xlsx`);
      setLoadingExcel(false);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      setLoadingExcel(false);
    }
  };

  useEffect(() => {
    refetch({
      campusId: campus?.id,
      page,
      limit,
      clusterid: selectedClusterId || availableClusters?.[0]?.id,
    });
  }, [clusters]);

  // Tour state
  const [isTourOpen, setIsTourOpen] = useState(false);

  useEffect(() => {
    const permissions = JSON.parse(localStorage.getItem("tourPermissions"));
    if (
      permissions?.showAllStudentsTour &&
      !loading &&
      !assignmentLoading &&
      !courseLoading
    ) {
      setTimeout(() => setIsTourOpen(true), 800);
    }
  }, [loading, assignmentLoading, courseLoading]);

  const tourSteps = [
    {
      selector: '[data-tour="cluster-select"]',
      content: " Use this filter to select a batch/cluster and view all students in that group.",
    },
    {
      selector: '[data-tour="download-btn"]',
      content: "⬇ Click here to download all student assignment and course scores as an Excel file.",
    },
  ];

  const closeTour = (disableAll = false) => {
    setIsTourOpen(false);
    const permissions = JSON.parse(localStorage.getItem("tourPermissions"));
    if (permissions) {
      if (disableAll) {
        // X button clicked - disable all tours
        permissions.showStudentLookupTour = false;
        permissions.showStaffReportTour = false;
        permissions.showAllStudentsTour = false;
        permissions.showStaffDashboardTabsTour = false;
      } else {
        // Got it clicked - only disable this tour
        permissions.showAllStudentsTour = false;
      }
      localStorage.setItem("tourPermissions", JSON.stringify(permissions));
    }
  };

  const openTour = () => {
    setIsTourOpen(true);
  };



  if (
    loading ||
    assignmentLoading ||
    courseLoading ||
    clusterLoading ||
    staffLoading ||
    campusLoading
  )
    return (
      <AdminLayout>
        <div className="max-w-full p-8">
          <div className="flex flex-col items-center justify-center h-screen gap-2">
            <LightSpinner message="Loading data, please wait" />
            
          </div>
        </div>
      </AdminLayout>
    );

  if (error)
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          Error: {error.message}
        </div>
      </AdminLayout>
    );

  return (
    <AdminLayout>
      <ModalUI
        open={showConfirmModal}
        heading="Load all student data?"
        subtitle="This can take time for large batches."
        items={[
          {
            title: "Includes complete data",
            desc: "This page loads all assignment scores and all course progress for every student in the selected batch.",
          },
          {
            title: "Use when needed",
            desc: "Continue only when you need full student-wise assignment and course data.",
          },
        ]}
        secondaryButtonText="No"
        primaryButtonText="Yes"
        onClose={() => navigate(-1)}
        onConfirm={() => {
          setShowConfirmModal(false);
          setShouldFetch(true);
        }}
        showOverlay
      />

      {shouldFetch && (
        <>
          <ReusableTour
            steps={tourSteps}
            isOpen={isTourOpen}
            onRequestClose={closeTour}
          />
          <div className="max-w-full min-h-screen pb-8 bg-white shadow-md sm:p-0 md:p-0 rounded-b-xl">
        <div className="max-w-full px-6 pt-3 pb-3 bg-white border-gray-200 shadow-md border-x sm:px-6">
          <div className="relative w-full sm:w-[760px] max-w-full overflow-hidden rounded-xl border border-sky-300 bg-sky-100 px-5 py-4 shadow-sm min-h-[96px]">
            <div className="absolute inset-y-0 left-0 w-1 bg-sky-900" />
            <div className="flex items-center gap-2.5 pl-2">
              <div className="flex items-center justify-center flex-shrink-0 bg-white border rounded-full shadow-sm w-9 h-9 border-sky-200">
                <LuMousePointerClick className="text-sky-900" size={18} />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold leading-tight text-slate-800 sm:text-lg">
                  View Assignment and Course
                </h3>
                <p className="mt-1 text-sm font-medium text-slate-700 sm:text-base">
                  {typedHelperText}
                  {typedHelperText.length < helperMessage.length && (
                    <span className="inline-block w-0.5 h-4 ml-0.5 bg-slate-700 animate-pulse"></span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Cluster dropdown + Date Filter + Search + Download */}
        <div className="max-w-full p-4 mb-0 bg-white shadow-md sm:p-6 rounded-t-xl">
          <div className="flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-center sm:gap-4 font-poppins">
            <div className="flex flex-col items-stretch w-full gap-3 sm:flex-row sm:items-end sm:gap-3 sm:w-auto">
              <div data-tour="cluster-select" className="w-full sm:w-48">
                <p className="mb-1 text-sm font-semibold text-gray-600">Batch details</p>
                <ReactSelect
                  isSearchable={false}
                  isDisabled={sortedClusters?.length == 1}
                  options={clusterOptions}
                  value={clusterOptions.find((option) => option.value === selectedClusterId) || clusterOptions[0] || null}
                  onChange={(option) => handleClusterChange(option?.value || "")}
                  styles={staffFilterSelectStyles}
                />
              </div>
              <div className="w-full sm:w-48">
                <p className="mb-1 text-sm font-semibold text-gray-600">Date filter</p>
                <ReactSelect
                  isSearchable={false}
                  options={dateFilterOptions}
                  value={dateFilterOptions.find((option) => option.value === dateFilter) || dateFilterOptions[0]}
                  onChange={(option) => setDateFilter(option?.value ?? "")}
                  styles={staffFilterSelectStyles}
                />
              </div>
              {dateFilter === "custom" && (
                <>
                  <input
                    type="date"
                    value={customFromDate}
                    onChange={(e) => setCustomFromDate(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg sm:text-base"
                    placeholder="From"
                  />
                  <input
                    type="date"
                    value={customToDate}
                    onChange={(e) => setCustomToDate(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg sm:text-base"
                    placeholder="To"
                  />
                </>
              )}
            </div>
            <div className="flex flex-col items-stretch w-full gap-3 sm:flex-row sm:items-center sm:gap-4 sm:w-auto">
              <div className="flex items-center justify-center gap-3 sm:gap-4">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 bg-blue-500 rounded sm:w-4 sm:h-4"></span>
                  <span className="text-xs font-medium text-gray-700 sm:text-sm">Assignment</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 bg-green-500 rounded sm:w-4 sm:h-4"></span>
                  <span className="text-xs font-medium text-gray-700 sm:text-sm">Course</span>
                </div>
              </div>
              <div className="relative w-full sm:w-auto">
                <input
                  type="text"
                  placeholder="Search by name or roll number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg sm:w-80 focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-base"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute text-gray-400 transform -translate-y-1/2 right-3 top-1/2 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
              <div data-tour="download-btn" className="w-full sm:w-auto">
                <div className="relative">
                  <button
                    onClick={() => setShowDownloadDropdown((prev) => !prev)}
                    className="inline-flex items-center justify-center transition-all w-9 h-9 text-slate-600 hover:text-slate-900 focus:outline-none"
                    title="Download"
                    aria-label="Download"
                  >
                    <FaDownload size={20} />
                  </button>

                  {showDownloadDropdown && (
                    <div className="absolute top-0 right-[calc(100%+10px)] z-20 w-[165px] rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_10px_24px_rgba(2,6,23,0.08)]">
                      <p className="mb-2 px-2 text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
                        Choose format
                      </p>
                      <button
                        onClick={() => {
                          exportToExcel();
                          if (!loadingExcel) {
                            setShowDownloadDropdown(false);
                          }
                        }}
                        disabled={loadingExcel}
                        className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors ${
                          loadingExcel ? "text-slate-400" : "text-slate-700 hover:bg-slate-100"
                        }`}
                      >
                        <span className={`rounded-lg p-1.5 ${loadingExcel ? "bg-slate-100 text-slate-400" : "bg-slate-100 text-slate-600"}`}>
                          <MdTableChart size={17} />
                        </span>
                        <span className="text-sm font-medium">Excel</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Table */}
        <div className="max-w-full px-2 overflow-x-auto bg-white border border-gray-200 shadow-xl sm:px-4 rounded-b-xl thin-scrollbar">
          {data?.getStudentsResults?.students.length !== 0 ? (
            <>
              <Table className="max-w-full  overflow-x-auto min-w-[800px]">
              <Table.Head className="shadow-sm bg-gradient-to-r from-blue-50 to-green-50 rounded-t-2xl">
                <Table.HeadCell className="px-3 py-3 text-xs font-bold text-center text-gray-800 sm:px-6 sm:py-4 sm:text-sm md:text-base whitespace-nowrap">Name</Table.HeadCell>
                <Table.HeadCell className="px-3 py-3 text-xs font-bold text-center text-gray-800 sm:px-6 sm:py-4 sm:text-sm md:text-base whitespace-nowrap">Roll No</Table.HeadCell>
                <Table.HeadCell className="px-3 py-3 text-xs font-bold text-center text-gray-800 sm:px-6 sm:py-4 sm:text-sm md:text-base whitespace-nowrap">Department</Table.HeadCell>
                {filteredAssignments.map((assignment) => (
                  <Table.HeadCell
                    key={assignment?.id}
                    className="px-3 sm:px-6 md:px-8 py-3 sm:py-4 text-xs sm:text-sm font-semibold text-center text-blue-800 bg-blue-100 border-b border-blue-200 min-w-[280px]"
                    title={assignment?.name}
                  >
                    <div>
                      {assignment?.name}
                    </div>
                    {assignmentOverallMarks[assignment.id] && (
                      <span className="block mt-1 text-[10px] sm:text-xs font-normal text-blue-600">
                        (Out of  {assignmentOverallMarks[assignment.id]})
                      </span>
                    )}
                  </Table.HeadCell>
                ))}
                {courseData?.getAllCoursesByCluster.map((course) => (
                  <React.Fragment key={course?.id}>
                    <Table.HeadCell
                      className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-semibold text-center text-green-800 bg-green-100 border-b border-green-200 max-w-[200px]"
                      title={course?.name}
                    >
                      <div className="truncate">
                        {course?.name}
                      </div>
                      <div className="mt-1 text-[10px] sm:text-xs font-normal text-green-700">(COURSE)</div>
                    </Table.HeadCell>
                    <Table.HeadCell
                      className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-semibold text-center text-green-800 bg-green-100 border-b border-green-200 max-w-[200px]"
                      title={course?.name}
                    >
                      <div className="truncate">
                        {course?.name}
                      </div>
                      <div className="mt-1 text-[10px] sm:text-xs font-normal text-green-700">(COURSE)</div>
                    </Table.HeadCell>
                  </React.Fragment>
                ))}
              </Table.Head>
              <Table.Body className="bg-white">
                {(data?.getStudentsResults?.students || [])
                  .filter((student) =>
                    student?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    student?.rollNo?.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((student, idx) => (
                  <Table.Row
                    key={student?.id}
                    className={`transition duration-150 ease-in-out hover:bg-blue-50/60 hover:shadow-md ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                    style={{ cursor: 'pointer' }}
                  >
                    <Table.Cell className="px-3 py-2 text-xs font-medium text-gray-900 sm:px-6 sm:py-3 sm:text-sm whitespace-nowrap">{student?.name}</Table.Cell>
                    <Table.Cell className="px-3 py-2 text-xs text-gray-700 sm:px-6 sm:py-3 sm:text-sm whitespace-nowrap">{student?.rollNo}</Table.Cell>
                    <Table.Cell className="px-3 py-2 text-xs text-gray-700 sm:px-6 sm:py-3 sm:text-sm whitespace-nowrap">
                      {student?.department && String(student.department).trim() !== "" ? student.department : "-"}
                    </Table.Cell>
                    {filteredAssignments.map((assignment) => {
                      const studentAssignment = student?.assignmentDetails?.find(
                        (assign) => assign.assignment_id === assignment?.id
                      );
                      return (
                        <Table.Cell
                          key={assignment?.id}
                          className="px-3 py-2 text-xs font-semibold text-center text-blue-900 border-b border-blue-100 sm:px-4 sm:py-3 sm:text-sm bg-blue-50 whitespace-nowrap"
                        >
                          {studentAssignment?.totalScore || "-"}
                        </Table.Cell>
                      );
                    })}
                    {courseData?.getAllCoursesByCluster.map((course) => {
                      const studentCourse = student?.courseDetails?.courseDetails.find(
                        (cd) => cd.courseId === course?.id
                      );
                      return (
                        <React.Fragment key={course?.id}>
                          <Table.Cell
                            className="px-3 py-2 text-xs font-semibold text-center text-green-900 border-b border-green-100 sm:px-4 sm:py-3 sm:text-sm bg-green-50 whitespace-nowrap"
                          >
                            {studentCourse?.completedQuestion}/
                            {studentCourse?.totalQuestion}
                          </Table.Cell>
                          <Table.Cell
                            className="px-3 py-2 text-xs font-semibold text-center text-green-900 border-b border-green-100 sm:px-4 sm:py-3 sm:text-sm bg-green-50 whitespace-nowrap"
                          >
                            {`${Math.floor(studentCourse?.percentageCompleted || 0)}%`}
                          </Table.Cell>
                        </React.Fragment>
                      );
                    })}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-[400px] sm:h-[500px] text-center px-4">
              <MdOutlinePersonOff className="mb-4 text-5xl text-gray-400 sm:text-7xl" />
              <h2 className="mb-2 text-lg font-bold text-gray-600 sm:text-xl md:text-2xl">
                Oops, No students are enrolled in this batch
              </h2>
              <p className="text-sm sm:text-base text-gray-500 mb-6 max-w-full sm:max-w-md lg:max-w-[500px]">
                It looks like we couldn't find any matching data. Try refining
                the filter options.
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        )}

        {/* Help button to reopen tour */}
        <button
          onClick={openTour}
          style={{ backgroundColor: "#053859" }}
          className="fixed z-10 flex items-center justify-center w-10 h-10 text-white rounded-full shadow-lg sm:w-10 sm:h-10 top-3 right-3 sm:top-4 sm:right-4 hover:bg-opacity-90 focus:ring-4 focus:ring-blue-300"
          aria-label="Help"
        >
          <span className="text-base font-bold sm:text-lg">?</span>
        </button>
      </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AllStudentsInCampus;
